import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const API_BASE_URL = 'https://api.neontradepro.com'

interface InvoiceCallbackProps {
  id: number
}

interface PlanData {
  id: number
  title: string
  duration: number
}

interface InvoiceData {
  id: number
  plan: PlanData
  trade_account_id: number
  price: number
  created_at: number
  status: 'success' | 'failed' | 'payment_pending'
}

const InvoiceCallback = ({ id }: InvoiceCallbackProps) => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<InvoiceData | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchInvoiceResult = async () => {
      setLoading(true)
      try {
        const response = await fetch(`${API_BASE_URL}/invoice/plan/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.detail || 'خطا در بارگذاری نتیجه صورتحساب')
        }
        const invoice = await response.json()
        setData(invoice)

        const invoiceStatus: string | undefined = invoice?.status
        const invoicePrice: number | undefined = invoice?.price
        const invoicePaidFlags = [invoice?.paid, invoice?.is_paid, invoice?.payment_status, invoice?.payment_state]

        const looksPaid =
          invoiceStatus === 'success' ||
          invoicePrice === 0 ||
          invoicePaidFlags.some(flag => flag === true || (flag && String(flag).toLowerCase().includes('paid')))

        if (!looksPaid) {
          console.debug('InvoiceCallback: invoice not considered paid', { invoice })
        }

        if (invoice && looksPaid) {
          try {
            const theme = localStorage.getItem('theme') || 'dark'
            let accountInfo: any = null
            try {
              const accResp = await fetch(`${API_BASE_URL}/trade-account/${invoice.trade_account_id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
              })
              if (accResp.ok) accountInfo = await accResp.json()
            } catch (e) {
              // ignore, we'll navigate with minimal params
              console.warn('Could not fetch trade account info for redirect', e)
            }

            const params = new URLSearchParams()
            params.set('theme', theme)
            params.set('planId', String(invoice.plan.id))
            params.set('price', String(invoice.price))
            params.set('periodLabel', `${invoice.plan.duration} روز`)
            params.set('accountValue', String(accountInfo?.account_value || 0))
            params.set('planName', invoice.plan.title || 'نامشخص')
            params.set('planDuration', String(invoice.plan.duration || 0))
            params.set('accountId', String(invoice.trade_account_id))
            if (accountInfo?.account_number) params.set('accountNumber', accountInfo.account_number)
            if (accountInfo?.account_server) params.set('accountServer', accountInfo.account_server)

            navigate(`/plan-settings?${params.toString()}`, { replace: true })
            return
          } catch (e) {
            console.error('Redirect to plan settings failed', e)
          }
        }
      } catch (err: any) {
        setError(err.message || 'خطا در ارتباط با سرور')
      } finally {
        setLoading(false)
      }
    }
    fetchInvoiceResult()
  }, [id, navigate])

  const statusConfig = {
    success: {
      icon: (
        <svg className='w-10 h-10 text-green-600' fill='none' stroke='currentColor' strokeWidth={2} viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' d='M5 13l4 4L19 7' />
        </svg>
      ),
      label: 'پرداخت موفق',
      color: 'text-green-600'
    },
    failed: {
      icon: (
        <svg className='w-10 h-10 text-red-600' fill='none' stroke='currentColor' strokeWidth={2} viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' />
        </svg>
      ),
      label: 'پرداخت ناموفق',
      color: 'text-red-600'
    },
    payment_pending: {
      icon: (
        <svg
          className='w-10 h-10 text-yellow-500'
          fill='none'
          stroke='currentColor'
          strokeWidth={2}
          viewBox='0 0 24 24'
        >
          <path strokeLinecap='round' strokeLinejoin='round' d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' />
        </svg>
      ),
      label: 'در انتظار پرداخت',
      color: 'text-yellow-500'
    }
  }

  if (error) return <div className='text-center text-red-500'>{error}</div>
  if (loading) return <div className='text-center'>در حال بارگزاری...</div>
  if (!data) return <div className='text-center'>اطلاعاتی یافت نشد.</div>

  const status = statusConfig[data.status]

  return (
    <div className='max-w-md mx-auto mt-10 p-6 bg-white rounded-2xl shadow-lg text-center space-y-4'>
      <div className='flex justify-center'>{status.icon}</div>
      <h2 className={`text-xl font-semibold ${status.color}`}>{status.label}</h2>

      <div className='text-gray-700 space-y-1'>
        <p>
          <span className='font-bold'>پلن: </span>
          {data.plan.title}
        </p>
        <p>
          <span className='font-bold'>مدت زمان: </span>
          {data.plan.duration} روز
        </p>
        <p>
          <span className='font-bold'>مبلغ: </span>${data.price / 100}
        </p>
        <p>
          <span className='font-bold'>تاریخ: </span>
          {new Date(data.created_at * 1000).toLocaleString('fa-IR')}
        </p>
      </div>

      <button
        onClick={() => navigate('/dashboard', { state: { refreshAccounts: true } })}
        className='mt-4 px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition'
      >
        بازگشت به پنل کاربری
      </button>
    </div>
  )
}

export default InvoiceCallback